import type { BlockContent, LayoutColumnDefinition } from '@atlaskit/adf-schema';

import {
	isHeaderElement,
	type ParsedStructuredElementType,
	type SectionElementType,
} from '../layout/types';

import type { AdfContentNode, TagConverter } from './types';

/**
 * Splits a section into its component parts: header and content children.
 *
 * Returns an object containing:
 * - header: The header element (h tag) if present, otherwise undefined
 * - contentChildren: All non-header child elements (excluding strings)
 *
 * Note: String children are silently dropped during this split. The ParsedElementType
 * currently includes string as a union member, which may benefit from tightening.
 *
 * @param section The section element to split
 * @returns Object with header and contentChildren properties
 */
const splitSectionParts = (
	section: SectionElementType,
): {
	contentChildren: ParsedStructuredElementType[];
	header: ParsedStructuredElementType | undefined;
} => {
	const children = Array.isArray(section.children) ? section.children : [];
	const header = children.find(isHeaderElement);
	const contentChildren = children.filter(
		(child): child is ParsedStructuredElementType =>
			!isHeaderElement(child) && typeof child !== 'string',
	);
	return { header, contentChildren };
};

/**
 * Converts children to an array of ADF nodes, filtering out undefined results.
 */
const convertChildrenToNodes = (
	children: ParsedStructuredElementType[],
	convertTag: TagConverter,
): AdfContentNode[] => {
	return children.flatMap((child) => convertTag(child) ?? []);
};

/**
 * Converts the layout content of a section based on its layout type attribute.
 *
 * Supports:
 * - "one-column" / "overview": Simple content flow (returns array of nodes)
 * - "two-columns": Left/right layout with 50/50 width (returns single layoutSection node)
 * - "two-rows": Top/bottom flow (returns array of nodes in order)
 *
 * Returns an array of ADF content nodes.
 */
const convertLayoutContent = (
	section: SectionElementType,
	contentChildren: ParsedStructuredElementType[],
	convertTag: TagConverter,
): AdfContentNode[] => {
	const layoutType = section.attributes?.layout;

	switch (layoutType) {
		case 'one-column':
		case 'overview': {
			// For single column, convert all content as-is (no layout wrapper needed)
			return convertChildrenToNodes(contentChildren, convertTag);
		}

		case 'two-columns': {
			// Build layout columns using pos attribute,
			// falling back to array order when pos is not present.
			const content = contentChildren.reduce<[LayoutColumnDefinition, LayoutColumnDefinition]>(
				(acc, child, index) => {
					const node = convertTag(child);
					if (node) {
						// Use pos as source of truth when present, otherwise fall back to array order
						const pos = child.attributes?.pos;
						const isLeft = pos !== undefined ? pos === 'left' : index === 0;
						acc[isLeft ? 0 : 1].content.push(node as BlockContent);
					}
					return acc;
				},
				[
					{ type: 'layoutColumn', attrs: { width: 50 }, content: [] },
					{ type: 'layoutColumn', attrs: { width: 50 }, content: [] },
				],
			);

			return [{ type: 'layoutSection', content }];
		}

		case 'two-rows': {
			// Sort by pos attribute (top before bottom),
			// falling back to array order when pos is not present.
			const getPriority = (pos: string | undefined) =>
				pos === 'top' ? 0 : pos === 'bottom' ? 2 : 1;
			const sorted = [...contentChildren].sort(
				(a, b) => getPriority(a.attributes?.pos) - getPriority(b.attributes?.pos),
			);
			return convertChildrenToNodes(sorted, convertTag);
		}

		default: {
			// Unknown layout type - return empty array
			return [];
		}
	}
};

/**
 * Converts a Lumina section tag to an array of ADF nodes.
 *
 * Acts as the main orchestrator that:
 * 1. Maintains the output array of ADF nodes
 * 2. Establishes the conversion steps in order
 * 3. Returns the accumulated ADF content nodes
 *
 * Conversion steps:
 * - Step 1: Extract and convert header element (if present)
 * - Step 2: Extract non-header children for content
 * - Step 3: Convert content based on layout type
 *
 * @param section The section element to convert
 * @param convertTag The converter function for section child elements
 * @returns Array of ADF content nodes
 */
export const convertSection = (
	section: SectionElementType,
	convertTag: TagConverter,
): AdfContentNode[] => {
	const outputNodes: AdfContentNode[] = [];

	// Step 1: Split section into header and content children
	const { header, contentChildren } = splitSectionParts(section);

	// Step 2: Convert and add header element (if present)
	if (header) {
		const headerNode = convertTag(header);
		if (headerNode) {
			outputNodes.push(headerNode);
		}
	}

	// Step 3: Convert content based on layout type
	const layoutNodes = convertLayoutContent(section, contentChildren, convertTag);
	outputNodes.push(...layoutNodes);

	return outputNodes;
};
